import { Button, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { FaCheck, FaPencilAlt, FaPlus } from "react-icons/fa";
import PopupModal from "../../PopupModal";
import { array, number, oneOf, string } from "prop-types";
import { displayNotification } from "helpers";
import CustomCKEditor from "../../CustomCKEditor";
import DropdownField from "../../core/DropdownField";
import AffirmationField from "../../core/AffirmationField";

const TableElementModifyPreviewModal = forwardRef((props, ref) => {
    const modalRef = useRef();

    const [data, setData] = useState({});
    
    useImperativeHandle(ref, () => {
        return {
            openModal: handleOpen
        };
    });

    const loadRowData = useCallback(() => {
        if (props.mode === "edit" && props.id != null && Object.values(data).length > 0) {
            const temp = { ...data };
            const check = localStorage.getItem(`pvsa_${props.templateId}_table_data_${props.tableId}`);
            if (check === null || JSON.parse(check)[props.id] === undefined) {
                displayNotification("warning", "Invalid row opened for editing");
                modalRef.current?.closeModal();
                return;
            }
            for (const x of Object.entries(JSON.parse(check)[props.id])) {
                temp[x[0]] = x[1];
            }
            setData(temp);
            modalRef.current?.openModal();
        }
    }, [props.mode, props.id, modalRef.current, props.templateId]);

    const handleSave = useCallback(
        (closeModal) => {
            const check = localStorage.getItem(`pvsa_${props.templateId}_table_data_${props.tableId}`);
            if (check === null && props.mode === "edit") {
                displayNotification("warning", "Cannot save invalid row which was opened for editing");
                modalRef.current?.closeModal();
                return;
            } else if (check === null && props.mode === "add") {
                const temp = [];
                temp.push(data);
                localStorage.setItem(`pvsa_${props.templateId}_table_data_${props.tableId}`, JSON.stringify(temp));
            } else {
                const temp = JSON.parse(check);
                if (props.mode === "edit") {
                    temp[props.id] = data;
                } else {
                    temp.push(data);
                }
                localStorage.setItem(`pvsa_${props.templateId}_table_data_${props.tableId}`, JSON.stringify(temp));
            }
            displayNotification("success", `Successfully ${props.mode === "add" ? "added" : "modified"} data`);
            window.dispatchEvent(new Event("pvsa-table-element-change"));
            closeModal();
        },
        [data, props.tableId, props.mode, props.id, props.templateId]
    );

    const resetDataState = useCallback(() => {
        if (props.columns.length > 0) {
            const temp = {};
            for (const x of props.columns) {
                temp[x.id] = "";
            }
            setData(temp);
        } else {
            setData({});
        }
    }, [props.columns]);

    const handleOpen = () => {
        props.mode === "edit" && loadRowData();
        modalRef.current?.openModal();
    }

    useLayoutEffect(() => {
        resetDataState();
    }, [props.columns, resetDataState]);

    return (
        <>
            <PopupModal
                ref={modalRef}
                title={props.mode === "edit" ? "Edit Row" : "Add New"}
                triggers={() =>
                    props.display === "menuitem" ? (
                        <MenuItem onClick={handleOpen}>
                            <ListItemIcon>{props.mode === "edit" ? <FaPencilAlt /> : <FaPlus />}</ListItemIcon>
                            <ListItemText>{props.mode === "edit" ? "Edit" : "Add"}</ListItemText>
                        </MenuItem>
                    ) : props.display === "button" ? (
                        <Button
                            variant={"contained"}
                            color="primary"
                            onClick={handleOpen}
                        >
                            {props.mode === "edit" ? (
                                <>
                                    <FaPencilAlt />
                                    <span className="ms-1">Edit</span>
                                </>
                            ) : (
                                <>
                                    <FaPlus />
                                    <span className="ms-1">Add</span>
                                </>
                            )}
                        </Button>
                    ) : (
                        ""
                    )
                }
                footTriggers={({closeModal}) => (
                    <Button variant={"contained"} color="success" onClick={() => handleSave(closeModal)}>
                        <FaCheck />
                        <span className="ms-1">Save</span>
                    </Button>
                )}
                onClose={resetDataState}
            >
                {props.columns.length > 0 ? (
                    <>
                        {props.columns.map((r) => (
                            <div className="row" key={r.id}>
                                <div className="col-4 mb-2">
                                    <strong>{r.name}</strong>
                                </div>
                                <div className="col-8 mb-2">
                                    {["text", "telephone"].includes(r.type) && (
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={data[r.id]}
                                            onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                        />
                                    )}
                                    {["number", "year", "percentage"].includes(r.type) && (
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={data[r.id]}
                                            onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                        />
                                    )}
                                    {["email"].includes(r.type) && (
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={data[r.id]}
                                            onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                        />
                                    )}
                                    {["date"].includes(r.type) && (
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={data[r.id]}
                                            onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                        />
                                    )}
                                    {["description"].includes(r.type) && (
                                        <CustomCKEditor
                                            value={data[r.id]}
                                            onChange={(val) => setData({ ...data, [r.id]: val })}
                                        />
                                    )}
                                    {["attachment"].includes(r.type) && (
                                        <input
                                            type="file"
                                            className="form-control"
                                            defaultValue={""}
                                            onChange={(e) => setData({ ...data, [r.id]: e.target.files[0] })}
                                        />
                                    )}
                                    {["affirmation"].includes(r.type) && (
                                        <AffirmationField
                                            label={r.name}
                                            isMinimal={false}
                                            hasNA={false}
                                            name={`affirmation_${r.id}`}
                                            value={data[r.id]}
                                            onChange={(e, val) =>
                                                setData({ ...data, [r.id]: val })
                                            }
                                        />
                                    )}
                                    {/* {["affirmation"].includes(r.type) && (
                                        <>
                                            <label>
                                                <input
                                                    type="radio"
                                                    className="form-control"
                                                    checked={data[r.id] === "yes"}
                                                    value={"yes"}
                                                    onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                                />
                                                <span className="ms-1">Yes</span>
                                            </label>
                                            <label className="ms-2">
                                                <input
                                                    type="radio"
                                                    className="form-control"
                                                    checked={data[r.id] === "no"}
                                                    value={"no"}
                                                    onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                                />
                                                <span className="ms-1">No</span>
                                            </label>
                                            <label className="ms-2">
                                                <input
                                                    type="radio"
                                                    className="form-control"
                                                    checked={data[r.id] === "na"}
                                                    value={"na"}
                                                    onChange={(e) => setData({ ...data, [r.id]: e.target.value })}
                                                />
                                                <span className="ms-1">NA</span>
                                            </label>
                                        </>
                                    )} */}
                                    {["list"].includes(r.type) && (
                                        <DropdownField
                                            plain={true}
                                            label={r.name}
                                            canAdd={false}
                                            templateId={props.templateId}
                                            listID={r.list_id}
                                            multiple={false}
                                            value={data[r.id]}
                                            onChange={(val) => setData({ ...data, [r.id]: val })}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    "No Columns Available"
                )}
            </PopupModal>
        </>
    );
});

TableElementModifyPreviewModal.propTypes = {
    mode: oneOf(["add", "edit"]).isRequired,
    display: oneOf(["button", "menuitem", "none"]).isRequired,
    id: number,
    templateId: string.isRequired,
    tableId: string.isRequired,
    columns: array.isRequired,
};

TableElementModifyPreviewModal.defaultProps = {
    mode: "add",
    display: "button",
    id: null,
    templateId: "",
    tableId: "",
    columns: [],
};

TableElementModifyPreviewModal.displayName = 'TableElementModifyPreviewModal';

export default TableElementModifyPreviewModal;
